import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver/dist/FileSaver';

class ExcelExportService {
  constructor() {
    // Esquema depurado: columnas Apify activas
    this.APIFY_COLUMNS = [
      'asin',
      'originalAsin',
      'título',
      'precio',
      'marca',
      'descripción',
      'Descripción general del producto',
      'características',
      'atributos',
      'atributos de variante',
      'Detalles de la variante',
      'variantesAsins',
      'Imágenes de alta resolución',
      'URL de producto no normalizada',
      'URL',
      'texto de ubicación',
      'Reseñas de la página del producto',
      'Información importante'
    ];

    // Anchos aproximados para mejor lectura
    this.columnWidths = this.APIFY_COLUMNS.map(col => {
      if (['título','descripción','Descripción general del producto','características','atributos','Reseñas de la página del producto','Información importante'].includes(col)) return { wch: 50 };
      if (['Imágenes de alta resolución','URL','URL de producto no normalizada'].includes(col)) return { wch: 60 };
      return { wch: 20 };
    });
  }

  // Generar Excel con las columnas Apify disponibles
  generateExcel(products, options = {}) {
    try {
      const {
        filename = `productos_apify_${new Date()?.toISOString()?.slice(0, 10)}_${Date.now()}.xlsx`,
        sheetName = 'Productos Apify'
      } = options;

      // Validate data
      if (!products || !Array.isArray(products)) {
        throw new Error('Products data must be an array');
      }

      if (products?.length === 0) {
        throw new Error('No products data to export');
      }

      // Estandarizar al esquema Apify activo
      const standardizedProducts = products?.map(p => {
        const row = {};
        this.APIFY_COLUMNS.forEach(col => {
          // Copiar respetando alias comunes
          switch (col) {
            case 'título': row[col] = p['título'] ?? p['titulo'] ?? p['title'] ?? ''; break;
            case 'descripción': row[col] = p['descripción'] ?? p['descripcion'] ?? p['description'] ?? ''; break;
            default: row[col] = p[col] ?? '';
          }
        });
        return row;
      });

      // Create workbook and worksheet
      const workbook = XLSX?.utils?.book_new();
      const worksheet = XLSX?.utils?.json_to_sheet(standardizedProducts);

      // Apply column widths
      worksheet['!cols'] = this.columnWidths;

  // Add some basic styling for headers
      const range = XLSX?.utils?.decode_range(worksheet?.['!ref']);
      
      for (let col = range?.s?.col; col <= range?.e?.col; col++) {
        const headerCell = XLSX?.utils?.encode_cell({ r: 0, c: col });
        if (worksheet?.[headerCell]) {
          worksheet[headerCell].s = {
            font: { bold: true },
            fill: { fgColor: { rgb: "E8F4FD" } },
            alignment: { horizontal: "center" }
          };
        }
      }

      // Add the worksheet to workbook
      XLSX?.utils?.book_append_sheet(workbook, worksheet, sheetName);

      // Generate buffer and create blob
      const excelBuffer = XLSX?.write(workbook, { 
        bookType: 'xlsx', 
        type: 'array',
        compression: true
      });
      
      const data = new Blob([excelBuffer], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      
      // Save file
      saveAs(data, filename);

      return {
        success: true,
        message: `Archivo Excel (${this.APIFY_COLUMNS.length} columnas) generado: ${filename}`,
        filename,
        recordCount: standardizedProducts?.length,
        fileSize: `${(data?.size / 1024)?.toFixed(1)} KB`
      };

    } catch (error) {
      console.error('Excel generation error:', error);
      return {
        success: false,
        message: `Failed to generate Excel file: ${error?.message}`,
        error: error?.message
      };
    }
  }

  // Validar estructura mínima (opcional)
  validateProductsData(products) {
    if (!Array.isArray(products)) {
      return { valid: false, error: 'Products data must be an array' };
    }

    if (products?.length === 0) {
      return { valid: false, error: 'No products to export' };
    }

    // Columnas mínimas sugeridas para el esquema actual
    const requiredColumns = [ 'título', 'precio' ];

    const firstProduct = products?.[0];
    const missingColumns = requiredColumns?.filter(col => 
      !firstProduct?.hasOwnProperty(col)
    );

    if (missingColumns?.length > 0) {
      return { 
        valid: false, 
        error: `Missing required columns: ${missingColumns?.join(', ')}` 
      };
    }

    return { valid: true };
  }

  // Plantilla de columnas activas
  getColumnTemplate() {
    return [...this.APIFY_COLUMNS];
  }
}

export default new ExcelExportService();